import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from 'store/slices/authSlice';
import { getApiUrl } from 'utils/config';
import { getAuthLoginUrl, getAuthCallbackUrl, getAuthClientId } from 'utils/config';
import { PageLoading, StandalonePage } from 'design/ui';

/**
 * Handles redirect from external auth service after sign-in.
 * Supports: ?code=... (OAuth auth code, exchanged for token via gateway) or ?token=JWT / ?access_token=JWT.
 */
const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  const finishLoginWithToken = async (token: string) => {
    try {
      localStorage.setItem('token', token);
      const response = await fetch(`${getApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const userData = await response.json();
        dispatch(
          loginSuccess({
            id: userData.user_id ?? userData.sub ?? '',
            email: userData.email ?? '',
            name: userData.name ?? '',
          })
        );
        window.location.href = '/';
        return;
      } else if (response.status === 401) {
        // Token invalid or expired – actually need to re-auth
        setError('Session invalid or expired. Redirecting to sign-in.');
        localStorage.removeItem('token');
        const loginUrl = getAuthLoginUrl();
        if (loginUrl) window.location.href = `${loginUrl.replace(/\/$/, '')}/login?client_id=${getAuthClientId()}&redirect_uri=${encodeURIComponent(getAuthCallbackUrl())}`;
        else navigate('/login', { replace: true });
      } else {
        // 5xx or other backend failure – don't force re-auth; keep token and enter app
        dispatch(loginSuccess({ id: 'user', email: '', name: '' }));
        window.location.href = '/';
      }
    } catch {
      // Network/CORS/transient error – don't send to login; keep token and enter app so user sees app errors
      dispatch(loginSuccess({ id: 'user', email: '', name: '' }));
      window.location.href = '/';
    }
  };

  useEffect(() => {
    const code = searchParams.get('code');
    const token = searchParams.get('token') || searchParams.get('access_token');

    if (code) {
      const redirectUri = getAuthCallbackUrl();
      fetch(`${getApiUrl()}/auth/token-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri }),
      })
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok) {
            setError(data.error_description || data.error || `Token exchange failed (${r.status}).`);
            return;
          }
          const accessToken = data.access_token;
          if (accessToken) {
            finishLoginWithToken(accessToken);
          } else {
            setError(data.error_description || data.error || 'No token returned.');
          }
        })
        .catch((err) => {
          setError(err.message || 'Token exchange failed.');
        });
      return;
    }

    if (token) {
      finishLoginWithToken(token);
      return;
    }

    setError('No code or token received. Redirecting to sign-in.');
    const loginUrl = getAuthLoginUrl();
    if (loginUrl) {
      window.location.href = `${loginUrl.replace(/\/$/, '')}/login?client_id=${getAuthClientId()}&redirect_uri=${encodeURIComponent(getAuthCallbackUrl())}`;
    } else {
      navigate('/login', { replace: true });
    }
    // Runs once per callback URL. `finishLoginWithToken` is a fresh closure each
    // render, so listing it would exchange the same one-time code twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dispatch]);

  if (error) {
    return (
      <StandalonePage title="Sign-in didn't finish">
        <p style={{ margin: 0 }}>{error}</p>
      </StandalonePage>
    );
  }

  return <PageLoading label="Signing you in…" />;
};

export default AuthCallback;
